import {
  BROLL_CARD_CAROUSEL,
  BROLL_CONTEXT_OVERLAY,
  BROLL_SERIAL_CARDS,
  BROLL_SPLIT_HALF,
  BROLL_SPLIT_HALF_HORIZONTAL,
  BROLL_TRIPLE_HORIZONTAL,
  BROLL_VISUAL_STYLES,
  OVERLAY_POSITIONS,
  brollTypeMeta,
  brollVisualStyleMeta,
  sanitizeBrollVisualStyle,
} from '@/lib/reelsShortsBroll';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Palette } from 'lucide-react';
import { cn } from '@/lib/utils';

function PhoneFrame({ children, className }) {
  return (
    <div
      className={cn(
        'relative mx-auto w-[72px] h-[128px] rounded-[10px] border border-white/15 overflow-hidden bg-black',
        className,
      )}
    >
      {children}
    </div>
  );
}

function SplitHalfPreview() {
  return (
    <PhoneFrame>
      <div className="absolute inset-0 flex">
        <div className="w-1/2 h-full bg-rose-500/40" />
        <div className="w-1/2 h-full bg-sky-400/30 flex items-center justify-center">
          <span className="text-[7px] text-sky-100">B-roll</span>
        </div>
      </div>
    </PhoneFrame>
  );
}

function SplitHalfHorizontalPreview() {
  return (
    <PhoneFrame>
      <div className="absolute inset-0 flex flex-col">
        <div className="h-1/2 bg-rose-500/40" />
        <div className="h-1/2 bg-sky-400/30 flex items-center justify-center">
          <span className="text-[7px] text-sky-100">B-roll</span>
        </div>
      </div>
    </PhoneFrame>
  );
}

function TriplePreview() {
  return (
    <PhoneFrame>
      <div className="absolute inset-0 flex flex-col">
        <div className="h-[22%] bg-amber-400/40 flex items-center justify-center text-[6px] text-amber-100">
          top
        </div>
        <div className="flex-1 bg-rose-500/45" />
        <div className="h-[22%] bg-emerald-400/35 flex items-center justify-center text-[6px] text-emerald-100">
          bottom
        </div>
      </div>
    </PhoneFrame>
  );
}

function OverlayPreview({ position = 'center' }) {
  const pos = String(position || 'center').replace('-', '_');
  const map = {
    top: 'top-1 left-1/2 -translate-x-1/2',
    bottom: 'bottom-1 left-1/2 -translate-x-1/2',
    left: 'left-1 top-1/2 -translate-y-1/2',
    right: 'right-1 top-1/2 -translate-y-1/2',
    center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    top_center: 'top-1 left-1/2 -translate-x-1/2',
    bottom_center: 'bottom-1 left-1/2 -translate-x-1/2',
  };
  return (
    <PhoneFrame>
      <div className="absolute inset-0 bg-rose-500/30" />
      <div className={cn('absolute w-6 h-6 artifact-sticker-motion artifact-sticker-motion-thump', map[pos] || map.center)}>
        <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-none">
          <path fill="#facc15" d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-6l-4 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
        </svg>
      </div>
    </PhoneFrame>
  );
}

function SerialCardsPreview({ cards = [], activeIndex = 0 }) {
  const list = cards.length ? cards : [{ label: 'A' }, { label: 'B' }, { label: 'C' }];
  return (
    <PhoneFrame className="w-[96px]">
      <div className="absolute inset-0 bg-rose-500/25" />
      <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-0.5 px-1">
        {list.slice(0, 4).map((card, idx) => (
          <span
            key={card.id || idx}
            className={cn(
              'h-5 min-w-[16px] px-0.5 rounded-[3px] text-[6px] flex items-center justify-center truncate',
              idx === activeIndex
                ? 'bg-amber-300/90 text-black scale-110 text-[7px] font-semibold'
                : 'bg-white/25 text-white text-[6px]',
            )}
          >
            {(card.label || '?').slice(0, 4)}
          </span>
        ))}
      </div>
    </PhoneFrame>
  );
}

export function ReelsShortsBrollPreview({ suggestion }) {
  const typeId = suggestion?.broll_type;
  if (typeId === BROLL_SPLIT_HALF) return <SplitHalfPreview />;
  if (typeId === BROLL_SPLIT_HALF_HORIZONTAL) return <SplitHalfHorizontalPreview />;
  if (typeId === BROLL_TRIPLE_HORIZONTAL) return <TriplePreview />;
  if (typeId === BROLL_SERIAL_CARDS || typeId === BROLL_CARD_CAROUSEL) {
    return <SerialCardsPreview cards={suggestion.cards || []} />;
  }
  if (typeId === BROLL_CONTEXT_OVERLAY) {
    return <OverlayPreview position={suggestion.position} />;
  }
  return <PhoneFrame><div className="absolute inset-0 bg-[#1a202c]" /></PhoneFrame>;
}

export function ReelsShortsBrollCatalog() {
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="reels-shorts-broll-catalog">
      { [
        { id: BROLL_SPLIT_HALF, node: <SplitHalfPreview /> },
        { id: BROLL_SPLIT_HALF_HORIZONTAL, node: <SplitHalfHorizontalPreview /> },
        { id: BROLL_TRIPLE_HORIZONTAL, node: <TriplePreview /> },
        { id: BROLL_CONTEXT_OVERLAY, node: <OverlayPreview position="top_center" /> },
        { id: BROLL_SERIAL_CARDS, node: <SerialCardsPreview /> },
        { id: BROLL_CARD_CAROUSEL, node: <SerialCardsPreview /> },
      ].map((row) => {
        const meta = brollTypeMeta(row.id);
        return (
          <div
            key={row.id}
            className="flex gap-3 rounded-xl border border-[#1f2635] bg-[#12151d] p-3"
          >
            {row.node}
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white">{meta?.label}</p>
              <p className="mt-1 text-[11px] text-slate-500 leading-snug">{meta?.description}</p>
              {row.id === BROLL_CONTEXT_OVERLAY ? (
                <p className="mt-1 text-[10px] text-rose-300/80">
                  {OVERLAY_POSITIONS.join(' · ')}
                </p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ReelsShortsBrollLookPicker({
  value,
  onChange,
  disabled = false,
  compact = false,
  testId = 'reels-shorts-broll-look',
}) {
  const styleId = sanitizeBrollVisualStyle(value);
  const meta = brollVisualStyleMeta(styleId);

  const select = (
    <Select value={styleId} onValueChange={(next) => onChange?.(next)} disabled={disabled}>
      <SelectTrigger
        className={cn(
          'bg-black/40 border-[#2a3446] text-xs text-white',
          compact ? 'h-9' : 'h-8',
        )}
        data-testid={testId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="z-[200] max-h-72">
        {BROLL_VISUAL_STYLES.map((row) => (
          <SelectItem key={row.value} value={row.value}>
            {row.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (compact) {
    return (
      <div className="space-y-1.5" data-testid={`${testId}-wrap`}>
        <span className="text-sm font-medium text-white">B-ROLL visual style</span>
        {select}
        {meta?.directive ? (
          <p className="text-[11px] text-slate-500 leading-snug line-clamp-2">{meta.directive}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-[#1f2635] bg-[#12151d] p-4 space-y-3"
      data-testid={`${testId}-panel`}
    >
      <div className="flex items-start gap-2">
        <Palette className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">B-ROLL visual style</p>
          <p className="text-[11px] text-slate-500">
            Look for generated B-ROLL images and stills. Change it, then regenerate the final video.
          </p>
        </div>
      </div>
      {select}
      {meta?.directive ? (
        <p className="text-[11px] text-slate-400 leading-snug">{meta.directive}</p>
      ) : null}
    </div>
  );
}
